import type { Analysis } from "../types/analysis";
import type { AnalyzedMeal } from "../types/analyzedMeal";
import type { Category, CategoryWithItems } from "../types/category";
import type { ICompanyInfo } from "../types/companyInfo";
import type { Item } from "../types/item";
import type { Meal } from "../types/meal";
import type { CompanyBase, CompanyFull } from "../types/company";
import type { searchItemParams } from "../types/searchItemParam";
import type { ISlider } from "../types/slider";
import type { ItemWithIngredients } from "../types/item";

const BASE_URL = "https://kaman.rest";

type ApiResponse<T> = {
  success: boolean;
  data: T;
  message?: string;
};

class ApiService {
  async fetchApi(endpoint: string) {
    try {
      const response = await fetch(`${BASE_URL}${endpoint}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        data: {},
        message: error instanceof Error ? error.message : "An error occurred",
      };
    }
  }

  async getAllCompanies(): Promise<CompanyBase[]> {
    return (await this.fetchApi("/api/companies")).companies;
  }

  async getCompanyInfo(companyId: string): Promise<CompanyFull> {
    const data = await this.fetchApi(
      `/api/company-info?company_id=${companyId}`
    );
    return data.company;
  }

  async getAllCategories(lang = "en"): Promise<Category[]> {
    return (await this.fetchApi(`/api/categories?lang=${lang}`)).categories;
  }

  async getCategoriesWithItems(
    companyId: string,
    lang = "en"
  ): Promise<CategoryWithItems[]> {
    return (
      await this.fetchApi(
        `/api/categories-with-items?company_id=${companyId}&lang=${lang}`
      )
    ).categories;
  }

  async getAllItems(lang = "en"): Promise<Item[]> {
    return (await this.fetchApi(`/api/all-items?lang=${lang}`)).items;
  }

  async getItemsWithIngredients(
    companyId: string,
    lang = "en"
  ): Promise<ItemWithIngredients[]> {
    return (
      await this.fetchApi(
        `/api/items-with-ingredients?company_id=${companyId}&lang=${lang}`
      )
    ).items;
  }

  async searchItems(params: searchItemParams): Promise<Item[]> {
    if (
      params.categoryIds &&
      Array.isArray(params.categoryIds) &&
      params.categoryIds.length > 1
    ) {
      const promises = params.categoryIds.map((categoryId) => {
        const searchParams = new URLSearchParams();

        if (params.search) searchParams.append("search", params.search);
        if (params.companyId && params.companyId !== "") {
          searchParams.append("company_id", params.companyId);
        }
        searchParams.append("category_id", categoryId);
        if (params.minPrice !== undefined)
          searchParams.append("min_price", params.minPrice.toString());
        if (params.maxPrice !== undefined)
          searchParams.append("max_price", params.maxPrice.toString());
        if (params.sortBy) searchParams.append("sort_by", params.sortBy);
        if (params.sortOrder)
          searchParams.append("sort_order", params.sortOrder);
        if (params.lang) searchParams.append("lang", params.lang);

        const url = `/api/items?${searchParams.toString()}`;

        return this.fetchApi(url).then((response) => response.items);
      });

      try {
        const responses = await Promise.all(promises);

        const allItems: Item[] = [];
        responses.forEach((response, index) => {
          if (response.success) {
            const items = response.data.items || response.data || [];
            allItems.push(...items);
          }
        });

        const uniqueItems = allItems.filter(
          (item, index, self) =>
            index === self.findIndex((i) => i.id === item.id)
        );

        if (params.sortBy === "price") {
          uniqueItems.sort((a, b) => {
            return params.sortOrder === "asc"
              ? a.price - b.price
              : b.price - a.price;
          });
        } else if (params.sortBy === "name") {
          uniqueItems.sort((a, b) => {
            return params.sortOrder === "asc"
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          });
        }

        return uniqueItems;
      } catch (error) {
        console.error("Error in multiple API calls:", error);
        return [];
      }
    } else {
      const searchParams: URLSearchParams = new URLSearchParams();

      if (params.search) searchParams.append("search", params.search);
      if (params.companyId && params.companyId !== "") {
        searchParams.append("company_id", params.companyId);
      }

      if (
        params.categoryIds &&
        Array.isArray(params.categoryIds) &&
        params.categoryIds.length === 1
      ) {
        searchParams.append("category_id", params.categoryIds[0]);
      } else if (params.categoryId) {
        searchParams.append("category_id", params.categoryId);
      }

      if (params.minPrice !== undefined)
        searchParams.append("min_price", params.minPrice.toString());
      if (params.maxPrice !== undefined)
        searchParams.append("max_price", params.maxPrice.toString());
      if (params.sortBy) searchParams.append("sort_by", params.sortBy);
      if (params.sortOrder) searchParams.append("sort_order", params.sortOrder);
      if (params.lang) searchParams.append("lang", params.lang);

      const url = `/api/items?${searchParams.toString()}`;

      return (await this.fetchApi(url)).items;
    }
  }

  async filterItems(companyId: string, categoryId: string): Promise<Item[]> {
    const params = new URLSearchParams({ company_id: companyId });
    if (categoryId) params.append("category_id", categoryId);

    return this.fetchApi(`/api/filter-items?${params.toString()}`);
  }

  async getSliders(companyId: string, lang = "en"): Promise<ISlider[]> {
    const data = await this.fetchApi(
      `/api/sliders?company_id=${companyId}&lang=${lang}`
    );
    return data.sliders;
  }

  async getPopularItems(
    companyId: string,
    limit = 10,
    lang = "en"
  ): Promise<Item[]> {
    return (
      await this.fetchApi(
        `/api/popular-items?company_id=${companyId}&limit=${limit}&lang=${lang}`
      )
    ).popular_items;
  }

  async getNewItems(
    companyId: string,
    limit = 10,
    lang = "en"
  ): Promise<Item[]> {
    return (
      await this.fetchApi(
        `/api/new-items?company_id=${companyId}&limit=${limit}&lang=${lang}`
      )
    ).new_items;
  }

  async analyzeMeals(meals: Meal[]): Promise<Analysis[]> {
    return [];
    // try {
    //   const response = await fetch(`/api/ai-analyze-meals`, {
    //     method: "POST",
    //     body: JSON.stringify({ meals }),
    //   });
    //   const data = await response.json();
    //   return data.analysis;
    // } catch (error) {
    //   // console.error("Error analyzing meals:", error);
    //   return [];
    // }
  }

  async getItem(itemId: string): Promise<Item> {
    return (await this.fetchApi(`/api/item-details?item_id=${itemId}`)).item;
  }
}

export const apiService = new ApiService();
